import { BookingType } from "../../Booking-Form/typings/booking-inputs";

/** Inline quick actions before progressive disclosure via the overflow menu. */
export const DASHBOARD2_QUICK_ACTIONS_VISIBLE_LIMIT = 3;

export type Dashboard2QuickActionDescriptor =
  | { type: "place"; bookingType: BookingType }
  | { type: "conference" }
  | { type: "visitor" };

type Dashboard2QuickActionOrderEntry = BookingType | "conference" | "visitor";

/** Product order for the quick-actions list (place types interleaved with room / visitor). */
export const DASHBOARD2_QUICK_ACTION_ORDER: readonly Dashboard2QuickActionOrderEntry[] = [
  BookingType.WORKPLACE,
  "conference",
  BookingType.VEHICLE,
  "visitor",
  BookingType.PARKINGPLACE,
  BookingType.ELECTRICCHARGINGSTATIONPLACE
];

type BuildDashboard2QuickActionDescriptorsParams = {
  placeBookingTypes: readonly BookingType[];
  conferenceZoneBookingEnabled: boolean;
  visitorManagementEnabled: boolean;
};

export function buildDashboard2QuickActionDescriptors({
  placeBookingTypes,
  conferenceZoneBookingEnabled,
  visitorManagementEnabled
}: BuildDashboard2QuickActionDescriptorsParams): Dashboard2QuickActionDescriptor[] {
  const licensedPlaceTypes = new Set(placeBookingTypes);

  return DASHBOARD2_QUICK_ACTION_ORDER.flatMap((entry): Dashboard2QuickActionDescriptor[] => {
    if (entry === "conference") {
      return conferenceZoneBookingEnabled ? [{ type: "conference" as const }] : [];
    }

    if (entry === "visitor") {
      return visitorManagementEnabled ? [{ type: "visitor" as const }] : [];
    }

    return licensedPlaceTypes.has(entry) ? [{ type: "place" as const, bookingType: entry }] : [];
  });
}

export function partitionDashboard2QuickActions<T>(
  items: readonly T[],
  visibleLimit = DASHBOARD2_QUICK_ACTIONS_VISIBLE_LIMIT
): { visible: T[]; overflow: T[] } {
  if (items.length <= visibleLimit) {
    return { visible: [...items], overflow: [] };
  }

  return {
    visible: items.slice(0, visibleLimit),
    overflow: items.slice(visibleLimit)
  };
}

export function dashboard2QuickActionKey(descriptor: Dashboard2QuickActionDescriptor): string {
  if (descriptor.type === "place") {
    return `place-${descriptor.bookingType}`;
  }

  return descriptor.type;
}

export function dashboard2QuickActionTestId(descriptor: Dashboard2QuickActionDescriptor): string {
  if (descriptor.type === "place") {
    return `dashboard2-book-place-${descriptor.bookingType}`;
  }

  if (descriptor.type === "conference") {
    return "dashboard2-book-a-room";
  }

  return "dashboard2-book-a-visitor";
}
